import { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { leftArrow, rightArrow, downArrow, exclamationIcon } from '../constants/icons';
import { sidebarIconMap } from '../constants/sidebarList';
import LinkWrapper from './LinkWrapper';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import RemindersSlider from './student-dashboard/card/RemindersSlider';
import LearnerHeading from './student-dashboard/card/LearnerHeader';
import { getProfileFieldBlocks } from '../../api/afterLogin/learners';
import { setSidebarData } from "../../reducers/sidebarSlice";

function MainSidebar({ sidebarList, allowedProducts, userSpEnabled }) {
  const { t } = useTranslation();
  const location = useLocation();
  const configData = useSelector((state) => state.config);
  const sidebarData = useSelector((state) => state.sidebar.isSidebarDisable);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [sidebarActive, setSidebarActive] = useState(false);

  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [totalSlides, setTotalSlides] = useState(0);
  const [profileFieldBlocks, setProfileFieldBlocks] = useState({});
  const learnerProfileFields = configData.session.us_profile_fields;
  const profileFields = learnerProfileFields && learnerProfileFields !== null ? learnerProfileFields.split("{#}") : false;
  const userProfileFieldData = {};
  const dispatch = useDispatch();
    useEffect(() => {
      const hasMissingMandatoryFields = Object.values(profileFieldBlocks || {}).some((block) =>
        block.profile_fields?.some((field) => {
          if (field.pf_mandatory === "1") {
            const fieldKey = field.id;
            return !userProfileFieldData[fieldKey] || userProfileFieldData[fieldKey] === "";
          }
          return false;
        })
      );

      const addressMandatory = configData?.address_mandatory === "1";
      const userAddress = configData?.session?.us_address;
      const isAddressMissing = !userAddress || Object.keys(userAddress).length === 0;

      const phoneMandatory = configData?.phno_mandatory === "1";
      const userPhone = configData?.session?.us_phone;
      const isPhoneMissing = !userPhone || userPhone.trim() === "";

      const shouldDisableSidebar =
        hasMissingMandatoryFields ||
        (addressMandatory && isAddressMissing) ||
        (phoneMandatory && isPhoneMissing);

      dispatch(setSidebarData({ isSidebarDisable: shouldDisableSidebar }));
    }, [profileFieldBlocks, learnerProfileFields, configData]);

  const handlePrevSlide = () => {
    sliderRef.current.slickPrev();
  };

  const handleNextSlide = () => {
    sliderRef.current.slickNext();
  };

  const handleSlideChange = (newSlideIndex) => {
    setCurrentSlide(newSlideIndex);
  };

  const handleTotalSlidesUpdate = (total) => {
    setTotalSlides(total);
  };

  const handleResize = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const isNotExpired = (expiryDate) => {
    return new Date(expiryDate) > new Date(); // Check if expiry date is in the future
  };

  const userSpEnabledOverride = (productId) => {
    const foundProduct = userSpEnabled && userSpEnabled.length > 0 ? userSpEnabled.find((userSp) => productId == userSp.uspa_sp_id) : false;
    if (foundProduct && foundProduct.uspa_sp_enabled === '1') {
      return true;
    }
    return false;
  };
  
  useEffect(() => {
    document.body.classList.add('myoneui-body');
    const sList = sidebarList.map((item) => {
      const sessionRoleId= configData?.session?.us_role_id ?? null;
      const foundProduct = allowedProducts.find((allowed) => item.slug === allowed.sp_uid);
      if (foundProduct && isNotExpired(foundProduct.expiry_date)) {
        const isAllowed = userSpEnabledOverride(foundProduct.id);
        if (sessionRoleId !== null && foundProduct?.sp_disabled_roles && foundProduct?.sp_disabled_roles !== null) {
          const disabledRoles = foundProduct?.sp_disabled_roles.includes(sessionRoleId);
          item.isVisible = disabledRoles ? false : true;
          if (isAllowed) {
            item.isVisible = true;
          }
        } else {
          item.isVisible = true;
        }
      }
      const isActivePath = location.pathname === item.link || location.pathname.startsWith(`${item.link}/`);
      // const isActivePath = location.pathname === item.link || location.pathname.includes(`${item.link}/`);
      if (isActivePath && Array.isArray(item.children) && item.children.length) {
        item.isActive = true;
        setSidebarActive(true);
      }

      if (foundProduct && foundProduct.sp_uid !== 'mykademy' && foundProduct.sp_uid !== 'tms' && foundProduct.sp_uid !== 'dms' && isNotExpired(foundProduct.expiry_date)) {
        if (foundProduct.sp_uid === 'eportfolio') {
          return { ...item, link: foundProduct.url + 'learner/program' };
        }
        return { ...item, link: foundProduct.url }; // Replace link if allowed and not expired
      }

      return item;
    });
    const modifiedList = [...sList];
    setSidebarItems(modifiedList.filter((item) => item.isVisible));
  }, [sidebarList, location.pathname, allowedProducts]);

  const [moreOptionsActive, setMoreOptionsActive] = useState(false);

  const onItemClick = (index, hasChildren, e) => {
    if (sidebarData) {
      e.preventDefault();
      return;
    }
    if (hasChildren) {
      e.preventDefault();
      let sbActive = false;
      const newItems = sidebarItems.map((item, i) => {
        if (i === index) {
          item.isActive = true;
          sbActive = true;
        } else {
          item.isActive = false;
        }
        return item;
      });
      setSidebarItems(newItems);
      setSidebarActive(sbActive);
    }
  };

  const onItemHideClick = (e) => {
    e.preventDefault();
    const newItems = sidebarItems.map((item, i) => {
      item.isActive = false;
      return item;
    });
    setSidebarItems(newItems);
    setSidebarActive(false);
  };

  const onMoreOptionsClick = (e) => {
    e.preventDefault();
    setMoreOptionsActive((active) => !active);
  };

  const childMapper = (child, j) => {
    if (child.type === 'divider') {
      return <li key={j} className="divider" />;
    }
    const isActivePath = location.pathname === child.link || location.pathname.includes(child.link) || (child.link && child.link.includes(location.pathname));
    if (child.children) {
      const moreOptionsList = child.children.map(childMapper);
      return (
        <li key={j} className={moreOptionsActive ? 'dropdown-option dropdown-option-active' : 'dropdown-option'}>
          <a onClick={onMoreOptionsClick} href="#top">
            {t(child.label)}
            {downArrow}
          </a>
          <ul>{moreOptionsList}</ul>
        </li>
      );
    }
    return (
      <li key={j} className={isActivePath ? 'active' : ''}>
        <LinkWrapper isReact={child.isReact} href={child.link || '#top'}>
          {t(child.label)}
          {child.children && rightArrow}
        </LinkWrapper>
      </li>
    );
  };

  const listSidebarItems = sidebarItems.map((item, i) => {
    let childItems = null;
    let itemHasChildren = false;

    //const isActivePath = location.pathname === item.link || location.pathname.includes(`${item.link}/`);
    // const isActivePath = location.pathname === item.link || (item.link && item.link.includes('/reports/'));
    const isActivePath = location.pathname === item.link || (item.link && location.pathname.startsWith(`${item.link}/`));

    if (item.children) {
      itemHasChildren = true;
      // const moreOptions = item.children.filter((n) => n.isMoreOptions);
      // const children = item.children.filter((n) => !n.isMoreOptions);
      const children = item.children;
      childItems = children.map(childMapper);
      // if (moreOptions.length) {
      //   const moreOptionsList = moreOptions.map(childMapper);
      //   childItems.push(
      //     <li key={childItems.length} className={moreOptionsActive ? 'dropdown-option dropdown-option-active' : 'dropdown-option'}>
      //       <a onClick={onMoreOptionsClick} href="#top">
      //         More Options
      //         {downArrow}
      //       </a>
      //       <ul>{moreOptionsList}</ul>
      //     </li>,
      //   );
      // }
    }

    if (item.type === 'divider') {
      return <li key={i} className="divider" />;
    }

    return (
      <li key={i} className={item.isActive || (isActivePath && !itemHasChildren) ? 'active' : ''}>
        <LinkWrapper href={item.link || '#top'} isReact={item.isReact} onClick={onItemClick.bind(null, i, itemHasChildren)} className="is-active">
          <div className="icon-wrap">
            {sidebarIconMap[item.code]}
            <span className="sidebar-hidden-item text-truncate">{t(item.label)}</span>
          </div>
          <div className="sidebar-hidden-item">{item.rightIcon ? sidebarIconMap[item.rightIcon] : item.children ? rightArrow : null}</div>
        </LinkWrapper>
        {item.children && (
          <div className="inner-sidebar">
            <div className="fixed-sidebar-back">
              <a className="sidebar-back-nav" onClick={onItemHideClick} href="#top">
                {leftArrow}
                {t(item.label)}
              </a>
              <span className="divider" />
            </div>
            <ul className="inner-sidebar-list">{childItems}</ul>
          </div>
        )}
      </li>
    );
  });

  return (
    <div className={`sidebar ${sidebarActive ? 'sidebar-active' : ''} ${totalSlides > 0 ? '' : 'no-reminder-slider'}`}>
      <ul className="sidebar-list">
        {listSidebarItems}
        {screenWidth > 1368 && (
          <div className="sidebar-slider">
            <LearnerHeading title="Reminders" svgIcon={exclamationIcon} />
            <RemindersSlider 
              ref={sliderRef}
              onSlideChange={handleSlideChange}
              onTotalSlidesUpdate={handleTotalSlidesUpdate} />
          </div>
        )}
        
      </ul>
    </div>
  );
}

export default MainSidebar;
